/**
 * Painel reutilizável de gestão de chaves BYOK.
 *
 * Usado em Settings > Integrações > Chaves de API. A página /llm-credentials
 * continua existindo como wrapper fino para retro-compat de bookmarks, mas
 * a entrada do menu lateral foi removida — BYOK é integração, vive em
 * Configurações.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { KeyRound, Plus, Trash2, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PROVIDER_LABEL: Record<string, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic (Claude)",
  google: "Google (Gemini)",
  openrouter: "OpenRouter",
};

const TEST_MODEL_BY_PROVIDER: Record<string, string> = {
  openai: "gpt-4o-mini",
  anthropic: "claude-haiku-4-5",
  google: "gemini-2.5-flash",
  openrouter: "anthropic/claude-haiku-4-5",
};

export default function LLMCredentialsPanel() {
  const utils = trpc.useUtils();
  const { data: credentials = [], isLoading } = trpc.llmCredentials.list.useQuery();

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<
    Record<number, { ok: boolean; latencyMs?: number; error?: string }>
  >({});

  const [form, setForm] = useState({
    label: "",
    provider: "openai" as "openai" | "anthropic" | "google" | "openrouter",
    apiKey: "",
    isDefault: false,
  });

  const createMutation = trpc.llmCredentials.create.useMutation({
    onSuccess: () => {
      utils.llmCredentials.list.invalidate();
      setCreateOpen(false);
      setForm({ label: "", provider: "openai", apiKey: "", isDefault: false });
      toast.success("Chave cadastrada e cifrada em repouso");
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Falha ao cadastrar a credencial.")),
  });

  const deleteMutation = trpc.llmCredentials.delete.useMutation({
    onSuccess: () => {
      utils.llmCredentials.list.invalidate();
      setDeleteId(null);
      toast.success("Chave removida");
    },
  });

  const testMutation = trpc.llmCredentials.test.useMutation();

  async function handleTest(id: number, provider: string) {
    setTestingId(id);
    try {
      const result = await testMutation.mutateAsync({
        id,
        model: TEST_MODEL_BY_PROVIDER[provider] ?? "gpt-4o-mini",
      });
      setTestResults((prev) => ({ ...prev, [id]: result }));
      if (result.ok) {
        toast.success(`Conexão OK (${result.latencyMs}ms)`);
      } else {
        toast.error(`Falhou: ${result.error}`);
      }
    } finally {
      setTestingId(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Cadastre suas próprias chaves de OpenAI, Anthropic, Google ou OpenRouter.
          Cada agente IA vai usar a chave que você associar — o LeadConversa
          não intermedia custo de IA. Chaves cifradas em repouso (AES-256-GCM).
        </p>
        <Button onClick={() => setCreateOpen(true)} className="gap-2 shrink-0">
          <Plus className="w-4 h-4" />
          Nova chave
        </Button>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      ) : credentials.length === 0 ? (
        <Card className="border-dashed border-2 border-border/50">
          <CardContent className="flex flex-col items-center justify-center py-10 gap-3">
            <KeyRound className="w-10 h-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground text-center">
              Nenhuma chave cadastrada. Cadastre uma para começar a usar Agentes IA.
            </p>
            <Button onClick={() => setCreateOpen(true)} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" />
              Cadastrar primeira chave
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {credentials.map((c) => {
            const test = testResults[c.id];
            return (
              <Card key={c.id} className="border-border/50">
                <CardContent className="pt-4 pb-4 flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{c.label}</span>
                        {c.isDefault && (
                          <Badge variant="outline" className="text-xs">Default</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {PROVIDER_LABEL[c.provider] ?? c.provider}
                      </p>
                      <p className="text-xs font-mono text-muted-foreground/80 mt-1">
                        {c.maskedKey}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTest(c.id, c.provider)}
                        disabled={testingId === c.id}
                        className="gap-1.5"
                      >
                        {testingId === c.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : test?.ok ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                        ) : test?.ok === false ? (
                          <AlertCircle className="w-3.5 h-3.5 text-red-600" />
                        ) : null}
                        Testar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteId(c.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  {test && (
                    <p
                      className={`text-xs ${test.ok ? "text-green-600" : "text-red-600"}`}
                    >
                      {test.ok ? `OK — ${test.latencyMs}ms` : `Erro: ${test.error}`}
                    </p>
                  )}
                  {c.lastUsedAt && (
                    <p className="text-xs text-muted-foreground">
                      Último uso: {new Date(c.lastUsedAt).toLocaleString("pt-BR")}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create dialog (mantemos modal só pra criação rápida — diálogo curto,
          não é form complexo) */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="w-[95vw] max-w-md mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-500" />
              Nova chave de API
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>Nome / rótulo</Label>
              <Input
                placeholder="Ex: OpenAI produção, Claude testes…"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Provider</Label>
              <Select
                value={form.provider}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, provider: v as typeof f.provider }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="openai">OpenAI</SelectItem>
                  <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                  <SelectItem value="google">Google (Gemini)</SelectItem>
                  <SelectItem value="openrouter">OpenRouter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Chave de API</Label>
              <Input
                type="password"
                placeholder="sk-… ou similar"
                value={form.apiKey}
                onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Cifrada (AES-256-GCM) antes de salvar e nunca exibida em claro
                novamente.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(e) =>
                  setForm((f) => ({ ...f, isDefault: e.target.checked }))
                }
              />
              Usar como padrão da conta
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => createMutation.mutate(form)}
              disabled={
                !form.label.trim() ||
                !form.apiKey.trim() ||
                createMutation.isPending
              }
            >
              {createMutation.isPending ? "Salvando…" : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir chave</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é permanente. Agentes que usam esta chave deixarão
              de funcionar até serem associados a outra credencial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                deleteId && deleteMutation.mutate({ id: deleteId })
              }
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
